import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  HiPlus,
  HiTrash,
  HiPencil,
  HiArrowRightOnRectangle,
  HiXMark,
  HiCheckCircle
} from 'react-icons/hi2';
import { Modal, ModalHeader, ModalBody } from '../ui/Modal';
import { Input, Select } from '../ui/Input';
import { VBButton } from '../ui/VBButton';
import { Badge } from '../ui/Badge';
import { PillTabs } from '../ui/PillTabs';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { getBuildInfo } from '../../../utils/buildInfo';
import { ContentSkeleton } from '../ui/ContentSkeleton';
import { useAuth } from '../../providers/AuthProvider';
import { useOrg } from '../../providers/OrgProvider';
import { useSettings } from '../../providers/SettingsProvider';
import {
  betterAuthHasPassword,
  betterAuthSetPassword,
  betterAuthChangePassword
} from '../../services/auth';
import { MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from '../../services/auth-constants';
import * as serverPhones from '../../../api/server/phones';
import { listChannels, Channel } from '../../../api/server/channels';
import { updateCurrentUser } from '../../../api/server/users';
import { logger } from '../../../utils/logger';
import { normalizePhone, isValidE164 } from '../../../utils/phone';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'details' | 'security' | 'phones';

type PhoneNumber = serverPhones.PhoneNumber;

export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  // V2 auth, org, and settings state
  const { user: authUser, logout, refreshUser } = useAuth();
  const { orgsMap, currentOrg } = useOrg();
  const { currentUser } = useSettings();

  const buildInfo = getBuildInfo();

  // Cross-org channels: fetch channels from ALL user orgs for Text-to-Channel
  const [allChannels, setAllChannels] = useState<Channel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);

  const multiOrg = authUser ? authUser.orgIds.length > 1 : false;

  const loadAllChannels = useCallback(async () => {
    if (!authUser?.orgIds?.length) return;
    setChannelsLoading(true);
    try {
      const results = await Promise.all(
        authUser.orgIds.map((orgId) => listChannels(orgId).catch(() => []))
      );
      setAllChannels(results.flat());
    } catch (err) {
      logger.error('[ProfileModal] Failed to load cross-org channels:', err);
    } finally {
      setChannelsLoading(false);
    }
  }, [authUser?.orgIds]);

  // Build channel lookup map for table display
  const allChannelsMap = useMemo(() => {
    const map: Record<string, Channel> = {};
    for (const ch of allChannels) {
      map[ch.channel_id] = ch;
    }
    return map;
  }, [allChannels]);

  // Build select options — prefix with org name when user has multiple orgs
  const channelOptions = useMemo(() => {
    return allChannels.map((ch) => {
      const orgName = orgsMap[ch.org_id]?.name;
      const label = multiOrg && orgName ? `${orgName} — ${ch.name}` : ch.name;
      return { value: ch.channel_id, label };
    });
  }, [allChannels, orgsMap, multiOrg]);

  // Merge user data from V2 auth with settings currentUser fallback
  const userName = authUser?.name || currentUser?.name || '';
  const userEmail = authUser?.email || currentUser?.email || '';
  const userImage = authUser?.image || currentUser?.image || '';
  const userId = authUser?.id || currentUser?.user_id || '';

  // Build org list with roles for display
  const userOrgs = useMemo(() => {
    if (!authUser?.orgIds) return [];
    return authUser.orgIds.map((orgId) => {
      const org = authUser.orgs?.[orgId] || orgsMap[orgId];
      return {
        id: orgId,
        name: org?.name || orgId,
        role: org?.role || 'viewer',
        isCurrent: currentOrg?.org_id === orgId
      };
    });
  }, [authUser?.orgIds, authUser?.orgs, orgsMap, currentOrg?.org_id]);

  // Local state
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [name, setName] = useState('');
  const [image, setImage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingImage, setIsEditingImage] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Phone numbers state
  const [phones, setPhones] = useState<PhoneNumber[]>([]);
  const [phonesLoading, setPhonesLoading] = useState(false);
  const [phonesLoaded, setPhonesLoaded] = useState(false);

  // Phone form state (inline, not modal)
  const [phoneFormMode, setPhoneFormMode] = useState<'add' | 'edit' | 'verify' | null>(null);
  const [selectedPhone, setSelectedPhone] = useState<PhoneNumber | null>(null);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newPhoneChannel, setNewPhoneChannel] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneActionLoading, setPhoneActionLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // Security tab state
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordSuccessVerb, setPasswordSuccessVerb] = useState<'set' | 'changed'>('set');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  // Confirm dialogs
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeletePhoneConfirm, setShowDeletePhoneConfirm] = useState(false);
  const [phoneToDelete, setPhoneToDelete] = useState<PhoneNumber | null>(null);

  // Initialize form with user data; reset phonesLoaded so skeleton shows on reopen
  useEffect(() => {
    if ((authUser || currentUser) && isOpen) {
      setName(userName);
      setImage(userImage);
      setError('');
      setPhonesLoaded(false);
    }
  }, [authUser, currentUser, userName, userImage, isOpen]);

  // Load phone numbers when phones tab is active
  const loadPhones = useCallback(async () => {
    logger.info('[ProfileModal] Loading phones...');
    setPhonesLoading(true);
    try {
      const phoneList = await serverPhones.listPhones();
      logger.info('[ProfileModal] Phones loaded:', phoneList);
      setPhones(phoneList);
      setPhonesLoading(false);
      setPhonesLoaded(true);
    } catch (err) {
      logger.error('[ProfileModal] Failed to load phones:', err);
      setPhones([]);
      setPhonesLoading(false);
      setPhonesLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'phones' && isOpen) {
      setPhonesLoaded(false);
      loadPhones();
      loadAllChannels();
    }
  }, [activeTab, isOpen, loadPhones, loadAllChannels]);

  // Get channel name — include org prefix for multi-org users
  const getChannelName = (channelId: string) => {
    const channel = allChannelsMap[channelId];
    if (!channel) return '[Unknown channel]';
    if (multiOrg) {
      const orgName = orgsMap[channel.org_id]?.name;
      return orgName ? `${orgName} — ${channel.name}` : channel.name;
    }
    return channel.name;
  };

  // Focus inputs when entering edit mode
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  useEffect(() => {
    if (isEditingImage && imageInputRef.current) {
      imageInputRef.current.focus();
      imageInputRef.current.select();
    }
  }, [isEditingImage]);

  // Handle profile save
  const handleSaveProfile = async () => {
    if (!userId) return;

    setIsSaving(true);
    setError('');

    const trimmedImage = image.trim();
    if (trimmedImage && !trimmedImage.startsWith('https://')) {
      setError('Image URL must use HTTPS');
      setIsSaving(false);
      return;
    }

    try {
      await updateCurrentUser({
        name: name.trim(),
        image: trimmedImage || null
      });
      logger.info('[ProfileModal] Profile updated successfully');
      await refreshUser();
    } catch (err) {
      logger.error('[ProfileModal] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Inline name save — on blur or Enter
  const handleNameBlur = () => {
    setIsEditingName(false);
    if (name.trim() !== userName) {
      handleSaveProfile();
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setName(userName);
      setIsEditingName(false);
    }
  };

  // Inline image save — on blur or Enter
  const handleImageBlur = () => {
    setIsEditingImage(false);
    if (image.trim() !== userImage) {
      handleSaveProfile();
    }
  };

  const handleImageKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setImage(userImage);
      setIsEditingImage(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    onClose();
    await logout();
  };

  // Phone actions
  const handleVerifyPhone = (phone: PhoneNumber) => {
    setSelectedPhone(phone);
    setVerificationCode('');
    setPhoneFormMode('verify');
  };

  const handleCancelPhoneForm = () => {
    setPhoneFormMode(null);
    setSelectedPhone(null);
    setNewPhoneNumber('');
    setVerificationCode('');
    setPhoneError('');
  };

  // Initialize default channel when channels load (only if not already set)
  useEffect(() => {
    if (allChannels.length > 0) {
      setNewPhoneChannel((prev) => prev || allChannels[0]?.channel_id || '');
    }
  }, [allChannels]);

  const handleDeletePhone = (phone: PhoneNumber) => {
    setPhoneToDelete(phone);
    setShowDeletePhoneConfirm(true);
  };

  const confirmDeletePhone = async () => {
    if (!phoneToDelete) return;

    setPhoneActionLoading(true);
    try {
      await serverPhones.deletePhone(phoneToDelete.number);
      await loadPhones();
      setShowDeletePhoneConfirm(false);
      setPhoneToDelete(null);
    } catch (err) {
      logger.error('[ProfileModal] Failed to delete phone:', err);
      setPhoneError(err instanceof Error ? err.message : 'Failed to delete phone');
      setShowDeletePhoneConfirm(false);
      setPhoneToDelete(null);
    } finally {
      setPhoneActionLoading(false);
    }
  };

  const handlePhoneFormSubmit = async () => {
    logger.info('[ProfileModal] handlePhoneFormSubmit called', {
      phoneFormMode,
      newPhoneNumber,
      newPhoneChannel,
      selectedPhone: selectedPhone?.number
    });

    // Validate and normalize phone number for add mode
    let phoneToSubmit = '';
    if (phoneFormMode === 'add') {
      if (!newPhoneNumber.trim()) {
        setPhoneError('Phone number is required');
        return;
      }
      phoneToSubmit = normalizePhone(newPhoneNumber);
      if (!isValidE164(phoneToSubmit)) {
        setPhoneError('Enter a valid phone number (e.g. 2125551234 or +12125551234)');
        return;
      }
      if (!newPhoneChannel) {
        setPhoneError('Please select a channel');
        return;
      }
      setPhoneError('');
      setNewPhoneNumber(phoneToSubmit);
    }

    setPhoneActionLoading(true);

    try {
      if (phoneFormMode === 'verify' && selectedPhone) {
        // Verify existing phone
        logger.info('[ProfileModal] Verifying phone:', selectedPhone.number);
        const res = await serverPhones.verifyPhone(selectedPhone.number, verificationCode);
        logger.info('[ProfileModal] Phone verified:', res);
        await loadPhones();
        handleCancelPhoneForm();
      } else if (phoneToSubmit && phoneFormMode === 'add') {
        // Add new phone
        logger.info('[ProfileModal] Creating phone:', {
          phone: phoneToSubmit,
          channel_id: newPhoneChannel
        });
        const res = await serverPhones.createPhone({
          phone: phoneToSubmit,
          channel_id: newPhoneChannel
        });
        logger.info('[ProfileModal] Phone created:', res);
        await loadPhones();
        setNewPhoneNumber(''); // Clear form after adding
        setPhoneFormMode(null); // Close form
      } else {
        logger.warn('[ProfileModal] No action taken - conditions not met');
      }
    } catch (err) {
      logger.error('[ProfileModal] Phone action failed:', err);
      setPhoneError(err instanceof Error ? err.message : 'Failed to complete phone action');
    } finally {
      setPhoneActionLoading(false);
    }
  };

  // Load password status when security tab opens
  useEffect(() => {
    if (activeTab === 'security' && isOpen) {
      setPasswordLoading(true);
      setPasswordError('');
      setPasswordSuccess(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      betterAuthHasPassword()
        .then((has) => setHasPassword(has))
        .catch(() => setHasPassword(null))
        .finally(() => setPasswordLoading(false));
    }
  }, [activeTab, isOpen]);

  // Handle password set/change
  const handlePasswordSubmit = async () => {
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword.length > MAX_PASSWORD_LENGTH) {
      setPasswordError(`Password must not exceed ${MAX_PASSWORD_LENGTH} characters`);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordSubmitting(true);
    try {
      const result = hasPassword
        ? await betterAuthChangePassword(currentPassword, newPassword)
        : await betterAuthSetPassword(newPassword);

      if (!result.success) {
        setPasswordError(result.error || 'Failed to update password');
        return;
      }

      const verb = hasPassword ? 'changed' : 'set';
      logger.info('[ProfileModal] Password %s successfully', verb);
      setPasswordSuccessVerb(verb);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      // Refresh hasPassword state
      setHasPassword(true);
    } catch (err) {
      logger.error('[ProfileModal] Password error:', err);
      setPasswordError('An unexpected error occurred');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  // Get avatar initials
  const getInitials = () => {
    if (userName) {
      return userName.charAt(0).toUpperCase();
    }
    if (userEmail) {
      return userEmail.charAt(0).toUpperCase();
    }
    return '?';
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={!isSaving}>
        <ModalHeader onClose={onClose}>User Settings</ModalHeader>

        <ModalBody className="!p-0">
          {/* Tabs */}
          <div className="px-4 py-3 border-b border-vb-border dark:border-vb-border-dark">
            <PillTabs
              tabs={[
                { id: 'details', label: 'Profile' },
                { id: 'security', label: 'Security' },
                { id: 'phones', label: 'SMS to Channel' }
              ]}
              activeTab={activeTab}
              onChange={(id) => setActiveTab(id as TabType)}
            />
          </div>

          {/* Profile Tab */}
          {activeTab === 'details' && (
            <div className="p-4 md:p-6 min-h-[280px] flex flex-col">
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm dark:bg-red-900/30 dark:border-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {/* Explainer */}
              <div className="mb-5 p-3 rounded-lg bg-vb-primary/5 dark:bg-vb-primary/10 border border-vb-primary/20 dark:border-vb-primary/20">
                <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
                  Your profile is shared across all organizations. Click your name or avatar to make
                  changes.
                </p>
              </div>

              {/* Avatar + identity row */}
              <div className="flex items-center gap-4 mb-6">
                {/* Avatar with pencil overlay for image editing */}
                <button
                  onClick={() => setIsEditingImage(!isEditingImage)}
                  className="relative w-14 h-14 shrink-0 rounded-full bg-gradient-to-br from-vb-primary to-vb-primary-dark flex items-center justify-center group overflow-hidden"
                  aria-label="Change profile image"
                >
                  {userImage && userImage.startsWith('https://') ? (
                    <img src={userImage} alt={userName} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white text-xl font-semibold">{getInitials()}</span>
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                    <HiPencil className="w-4 h-4 text-white" />
                  </div>
                </button>
                <div className="min-w-0 flex-1">
                  {/* Name row — hidden when editing image, replaced by "Image URL" label */}
                  <div className="h-7 flex items-center">
                    {isEditingImage ? (
                      <span className="text-xs font-medium text-vb-text-secondary dark:text-vb-text-secondary-dark">
                        Image URL
                      </span>
                    ) : isEditingName ? (
                      <input
                        ref={nameInputRef}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={handleNameBlur}
                        onKeyDown={handleNameKeyDown}
                        maxLength={64}
                        disabled={isSaving}
                        className="text-base font-medium normal-case text-vb-text-primary dark:text-vb-text-primary-dark bg-transparent border-b border-vb-primary outline-none w-full leading-7"
                      />
                    ) : (
                      <button
                        onClick={() => setIsEditingName(true)}
                        className="flex items-center gap-2 group text-left normal-case leading-7"
                      >
                        <span className="text-base font-medium normal-case text-vb-text-primary dark:text-vb-text-primary-dark truncate">
                          {userName || 'Add name'}
                        </span>
                        {authUser?.role && authUser.role !== 'viewer' && (
                          <Badge
                            variant={authUser.role === 'super-admin' ? 'warning' : 'default'}
                            size="sm"
                            className="normal-case"
                          >
                            {authUser.role}
                          </Badge>
                        )}
                        <HiPencil className="w-3.5 h-3.5 shrink-0 text-vb-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity dark:text-vb-text-tertiary-dark" />
                      </button>
                    )}
                  </div>
                  <div className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark truncate">
                    {isEditingImage ? (
                      <input
                        ref={imageInputRef}
                        type="text"
                        value={image}
                        onChange={(e) => setImage(e.target.value)}
                        onBlur={handleImageBlur}
                        onKeyDown={handleImageKeyDown}
                        disabled={isSaving}
                        placeholder="https://..."
                        className="text-sm normal-case text-vb-text-primary dark:text-vb-text-primary-dark bg-transparent border-b border-vb-border dark:border-vb-border-dark focus:border-vb-primary outline-none w-full"
                      />
                    ) : (
                      userEmail || 'No email'
                    )}
                  </div>
                </div>
              </div>

              {/* Organizations */}
              {userOrgs.length > 0 && (
                <div className="mb-4">
                  <span className="block text-xs font-medium text-vb-text-secondary dark:text-vb-text-secondary-dark mb-2">
                    Organizations
                  </span>
                  <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
                    {userOrgs.map((org) => (
                      <div
                        key={org.id}
                        className="flex items-center justify-between py-1.5 px-3 rounded-md bg-vb-surface dark:bg-vb-surface-dark"
                      >
                        <span className="text-sm text-vb-text-primary dark:text-vb-text-primary-dark truncate">
                          {org.name}
                          {org.isCurrent && (
                            <span className="ml-1.5 text-xs text-vb-text-tertiary dark:text-vb-text-tertiary-dark">
                              (current)
                            </span>
                          )}
                        </span>
                        <Badge variant={org.role === 'admin' ? 'info' : 'default'} size="sm">
                          {org.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="p-4 md:p-6 min-h-[280px]">
              <div className="mb-5 p-3 rounded-lg bg-vb-primary/5 dark:bg-vb-primary/10 border border-vb-primary/20 dark:border-vb-primary/20">
                <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
                  {hasPassword
                    ? 'Change your account password. You will need your current password.'
                    : 'Set a password for your account to enable email+password sign in.'}
                </p>
              </div>

              {passwordLoading ? (
                <ContentSkeleton rows={3} />
              ) : (
                <>
                  {passwordSuccess && (
                    <div className="mb-4 p-3 rounded-lg bg-vb-success/10 border border-vb-success text-sm text-vb-success flex items-center gap-2">
                      <HiCheckCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
                      Password {passwordSuccessVerb} successfully.
                    </div>
                  )}

                  {passwordError && (
                    <div
                      className="mb-4 p-3 rounded-lg bg-vb-danger/10 border border-vb-danger text-sm text-vb-danger"
                      role="alert"
                    >
                      {passwordError}
                    </div>
                  )}

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handlePasswordSubmit();
                    }}
                    className="space-y-4"
                    aria-label={hasPassword ? 'Change password' : 'Set password'}
                  >
                    {hasPassword && (
                      <div>
                        <label
                          htmlFor="current-password"
                          className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                        >
                          Current Password
                        </label>
                        <Input
                          id="current-password"
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          required
                          fullWidth
                          autoComplete="current-password"
                          aria-required="true"
                          placeholder="Enter your current password"
                          disabled={passwordSubmitting}
                        />
                      </div>
                    )}

                    <div>
                      <label
                        htmlFor="new-password"
                        className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                      >
                        New Password
                      </label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        fullWidth
                        autoComplete="new-password"
                        aria-required="true"
                        placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                        disabled={passwordSubmitting}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="confirm-new-password"
                        className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
                      >
                        Confirm New Password
                      </label>
                      <Input
                        id="confirm-new-password"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        required
                        fullWidth
                        autoComplete="new-password"
                        aria-required="true"
                        placeholder="Repeat your new password"
                        disabled={passwordSubmitting}
                      />
                    </div>

                    <VBButton
                      variant="primary"
                      fullWidth
                      disabled={
                        passwordSubmitting ||
                        !newPassword ||
                        !confirmNewPassword ||
                        (hasPassword === true && !currentPassword)
                      }
                      onClick={handlePasswordSubmit}
                      aria-busy={passwordSubmitting}
                    >
                      {passwordSubmitting
                        ? hasPassword
                          ? 'Changing...'
                          : 'Setting...'
                        : hasPassword
                          ? 'Change Password'
                          : 'Set Password'}
                    </VBButton>
                  </form>
                </>
              )}
            </div>
          )}

          {/* SMS to Channel Tab */}
          {activeTab === 'phones' && (
            <div className="p-4 md:p-6 min-h-[280px]">
              {/* Explainer */}
              <div className="mb-5 p-3 rounded-lg bg-vb-primary/5 dark:bg-vb-primary/10 border border-vb-primary/20 dark:border-vb-primary/20">
                <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
                  Send text messages from your phone to a VoiceBrain channel. Register your number
                  below, pick which channel it feeds into, and verify via SMS.
                </p>
              </div>

              {/* Header */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-vb-text-primary dark:text-vb-text-primary-dark">
                  Linked Numbers
                </h3>
                {phoneFormMode !== 'add' && phoneFormMode !== 'verify' && (
                  <VBButton
                    variant="primary"
                    size="sm"
                    icon={<HiPlus className="w-4 h-4" />}
                    onClick={() => setPhoneFormMode('add')}
                  >
                    Link Phone
                  </VBButton>
                )}
              </div>

              {/* Phone List */}
              {phonesLoading || !phonesLoaded ? (
                <ContentSkeleton rows={3} />
              ) : phones.length > 0 ? (
                <div className="border border-vb-border dark:border-vb-border-dark rounded-lg divide-y divide-vb-border dark:divide-vb-border-dark">
                  {phones.map((phone) => (
                    <div key={phone.number}>
                      <div className="flex items-center gap-3 px-4 py-3">
                        <span className="font-mono text-sm text-vb-text-primary dark:text-vb-text-primary-dark flex-1">
                          {phone.number}
                        </span>
                        <span className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark hidden md:block">
                          {getChannelName(phone.channel_id || '')}
                        </span>
                        {phone.status === 'Verified' ? (
                          <Badge variant="success" dot>
                            Verified
                          </Badge>
                        ) : (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() => handleVerifyPhone(phone)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') handleVerifyPhone(phone);
                            }}
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            <Badge variant="warning" dot>
                              Not verified
                            </Badge>
                          </span>
                        )}
                        <div className="flex gap-1">
                          <VBButton
                            variant="ghost"
                            size="sm"
                            icon={<HiTrash className="w-4 h-4 text-vb-danger" />}
                            onClick={() => handleDeletePhone(phone)}
                            aria-label="Delete phone"
                          />
                        </div>
                      </div>
                      {/* Inline verify form for this phone */}
                      {phoneFormMode === 'verify' && selectedPhone?.number === phone.number && (
                        <div className="px-4 pb-3 flex gap-2 items-center">
                          <Input
                            type="text"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            placeholder="Verification code"
                            disabled={phoneActionLoading}
                            className="flex-1"
                          />
                          <VBButton
                            variant="primary"
                            size="sm"
                            onClick={handlePhoneFormSubmit}
                            disabled={phoneActionLoading || !verificationCode}
                          >
                            {phoneActionLoading ? 'Verifying...' : 'Verify'}
                          </VBButton>
                          <VBButton
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelPhoneForm}
                            aria-label="Cancel verification"
                          >
                            <HiXMark className="w-4 h-4" />
                          </VBButton>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : phoneFormMode !== 'add' ? (
                <div className="text-center py-8 text-vb-text-secondary dark:text-vb-text-secondary-dark">
                  <p className="mb-1">No phone numbers linked yet.</p>
                  <p className="text-xs text-vb-text-tertiary dark:text-vb-text-tertiary-dark">
                    Tap &ldquo;Link Phone&rdquo; to connect your number to a channel.
                  </p>
                </div>
              ) : null}

              {/* Link Phone Form - Shown when Link Phone button clicked */}
              {phoneFormMode === 'add' && (
                <div className="mt-4 p-4 bg-vb-surface dark:bg-vb-surface-dark rounded-lg border border-vb-border dark:border-vb-border-dark">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark">
                      Link Phone Number
                    </span>
                    <VBButton
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelPhoneForm}
                      aria-label="Cancel"
                    >
                      <HiXMark className="w-4 h-4" />
                    </VBButton>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div>
                      <Input
                        type="tel"
                        label="Phone Number"
                        value={newPhoneNumber}
                        onChange={(e) => {
                          setNewPhoneNumber(e.target.value);
                          if (phoneError) setPhoneError('');
                        }}
                        placeholder="+1234567890"
                        disabled={phoneActionLoading}
                      />
                      {phoneError && <p className="mt-1 text-xs text-vb-danger">{phoneError}</p>}
                    </div>
                    <div className="flex gap-2 items-end">
                      <Select
                        label="Channel"
                        value={newPhoneChannel}
                        onChange={(e) => setNewPhoneChannel(e.target.value)}
                        options={channelOptions}
                        disabled={phoneActionLoading || channelsLoading}
                        className="flex-1"
                      />
                      <VBButton
                        variant="primary"
                        onClick={handlePhoneFormSubmit}
                        disabled={phoneActionLoading || !newPhoneNumber}
                        className="shrink-0"
                      >
                        {phoneActionLoading ? 'Adding...' : 'Add'}
                      </VBButton>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer — shared across all tabs */}
          <div className="px-4 md:px-6 py-4 border-t border-vb-border dark:border-vb-border-dark flex items-center justify-between">
            <span
              className="text-xs text-vb-text-tertiary dark:text-vb-text-tertiary-dark"
              title={
                buildInfo.sha !== 'dev'
                  ? `${buildInfo.sha_short} · ${buildInfo.ref} · ${buildInfo.build_time}`
                  : 'Local development'
              }
            >
              v{buildInfo.version}
              {buildInfo.sha !== 'dev' && (
                <span className="ml-1 opacity-60">({buildInfo.sha_short})</span>
              )}
            </span>
            <div className="flex gap-3">
              <VBButton
                variant="danger"
                size="sm"
                icon={<HiArrowRightOnRectangle className="w-4 h-4" />}
                onClick={() => setShowLogoutConfirm(true)}
              >
                Log Out
              </VBButton>
              <VBButton variant="secondary" size="sm" onClick={onClose}>
                Close
              </VBButton>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Logout Confirmation */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="Log Out"
        message="Are you sure you want to log out?"
        confirmText="Log Out"
        variant="danger"
      />

      {/* Delete Phone Confirmation */}
      <ConfirmDialog
        isOpen={showDeletePhoneConfirm}
        onClose={() => {
          setShowDeletePhoneConfirm(false);
          setPhoneToDelete(null);
        }}
        onConfirm={confirmDeletePhone}
        title="Delete Phone"
        message={`Are you sure you want to delete ${phoneToDelete?.number}?`}
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};
